import { IconButton } from "@App/Components/common/IconButton/IconButton";
import { IconButtonSkeleton } from "@App/Components/common/IconButton/IconButtonSkeleton";
import { TireSparkleIcon } from "@App/Components/common/Icons";
import type { DhlServiceSelection } from "@App/Pages/Partner/DHL/serviceCategories";
import type { Service } from "@App/types";
import { getServiceIcon } from "@App/utils/getServiceIcon";
import type { ServiceGroup } from "@App/utils/groupServices";
import type { FunctionComponent } from "react";
import { useTranslation } from "react-i18next";

export type ServiceCategoryStepProps = {
  selection: DhlServiceSelection;
  loading: boolean;
  error: unknown;
  onSelectCategory: (category: ServiceGroup) => void;
  onSelectService: (service: Service) => void;
};

/**
 * DHL's first step: a tile per service category, followed by a tile per service no category claims.
 *
 * Only ever rendered for DHL, so unlike the shared `Partials/ServiceStep` it carries no
 * per-organization conditionals.
 */
export const ServiceCategoryStep: FunctionComponent<ServiceCategoryStepProps> = ({
  selection,
  loading,
  error,
  onSelectCategory,
  onSelectService,
}: ServiceCategoryStepProps) => {
  const { t } = useTranslation();

  const gridClasses = "md:w-fit ease-in-out grid grid-cols-1 xs:grid-cols-2 gap-2 mb-4 mt-4 w-full";

  if (loading) {
    return (
      <div className="overflow-hidden">
        <div className={gridClasses}>
          <IconButtonSkeleton />
          <IconButtonSkeleton />
          <IconButtonSkeleton />
          <IconButtonSkeleton />
        </div>
      </div>
    );
  }

  if (error) {
    return <div>{t("error")}</div>;
  }

  return (
    <div className="overflow-hidden">
      <div className={gridClasses}>
        {selection.categories.map((category) => (
          <IconButton
            key={category.label}
            title={category.label}
            onClick={() => onSelectCategory(category)}
            icon={<TireSparkleIcon primary className={"w-10 h-10"} />}
          />
        ))}
        {selection.services.map((service) => (
          <IconButton
            key={service.id}
            title={service.pivot.display_name ? service.pivot.display_name : service.name}
            subtitle={service.pivot.subtitle}
            onClick={() => onSelectService(service)}
            icon={getServiceIcon(service.pivot.icon)}
          />
        ))}
      </div>
    </div>
  );
};
